import { api } from './client';

// ============================================
// Auth Types
// ============================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  ai_debug_level?: 'none' | 'business' | 'developer';
}

export interface AuthResponse {
  token: string;
  expires_at: string;
  user: User;
}

export interface AIDebugLevelResponse {
  debug_level: 'none' | 'business' | 'developer';
}

// ============================================
// Auth API
// ============================================

export const authApi = {
  /**
   * Login with email and password
   */
  login: (data: LoginRequest) =>
    api.post<AuthResponse>('/auth/login', data),

  /**
   * Register a new user
   */
  register: (data: RegisterRequest) =>
    api.post<AuthResponse>('/auth/register', data),

  getAIDebugLevel: () =>
    api.get<AIDebugLevelResponse>('/me/ai-debug-level'),

  updateAIDebugLevel: (debugLevel: 'none' | 'business' | 'developer') =>
    api.put<AIDebugLevelResponse>('/me/ai-debug-level', { debug_level: debugLevel }),

  /**
   * Logout - clears local token, then redirects to gatekeeper to clear SSO session.
   * Follows RP-Initiated Logout pattern (same as Google account sign-out).
   */
  logout: async () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_info');
    // 主动退出：清除记忆期 cookie，下次需重新登录
    document.cookie = 'session_remember=; max-age=0; path=/; SameSite=Strict';
    import('../store/useAppStore').then(({ useAppStore }) => {
      useAppStore.setState({
        permissionCodes: [],
        permissionsLoaded: false,
        permissionsLoading: false,
        isLoggedOut: false,
      });
    });
    const gatekeeperUrl = ((import.meta.env.VITE_GATEKEEPER_URL as string | undefined) || 'https://test-gatekeeper.xmslol.cn').replace(/\/+$/, '');
    const clientId = (import.meta.env.VITE_OAUTH_CLIENT_ID as string | undefined) || 'ad-platform';
    const redirectUri = (import.meta.env.VITE_OAUTH_REDIRECT_URI as string | undefined) || `${window.location.origin}/sso/callback`;
    // 生成 PKCE，登录完直接继续 SSO 流程回到 ad-platform
    const { generateCodeVerifier, generateCodeChallenge, generateState } = await import('../lib/pkce');
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    const state = generateState();
    sessionStorage.setItem('oauth_code_verifier', verifier);
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_redirect_after', '/');
    const authorizeUrl = new URL(`${gatekeeperUrl}/api/oauth/authorize`);
    authorizeUrl.searchParams.set('client_id', clientId);
    authorizeUrl.searchParams.set('redirect_uri', redirectUri);
    authorizeUrl.searchParams.set('state', state);
    authorizeUrl.searchParams.set('code_challenge', challenge);
    authorizeUrl.searchParams.set('code_challenge_method', 'S256');
    // 退出后跳 authorize，gatekeeper session 已清除会自动转到 /login?next=<authorize>，登录完继续 OAuth
    window.location.href = `${gatekeeperUrl}/api/oauth/logout?post_logout_redirect_uri=${encodeURIComponent(authorizeUrl.toString())}`;
  },

  /**
   * Get the current access token
   */
  getToken: () => localStorage.getItem('access_token'),

  /**
   * Check if user is authenticated
   */
  isAuthenticated: () => !!localStorage.getItem('access_token'),
};
